import { ROUTE_NAMES } from '@/constant/routes'

const PAGE_RENDER_FROM_CACHE_EVENT = 'page_render_from_cache'

const MEASURED_SURFACES = [ROUTE_NAMES.HOME, ROUTE_NAMES.PRESENTATIONS, ROUTE_NAMES.PRESENTATION_DETAIL]

const SURFACE_DATA_REQUEST = '/api/presentation/'
const PROFILE_DATA_REQUEST = '/api/users/get-profile'
const PROFILE_SURFACE = 'profile'
const GIVE_UP_AFTER_MS = 30 * 1000

/**
 * Builds the page_render_from_cache properties for one page load, or null when the load did not
 * land on a measured surface. `shell_source` tells cached loads from network ones, so the same
 * event gives the before and after numbers.
 */
const buildPageRenderProperties = ({
  routeName, paintEntries, resourceEntries, isControlledByServiceWorker,
}) => {
  if (!MEASURED_SURFACES.includes(routeName)) return null
  const firstRender = paintEntries.find((entry) => entry.name === 'first-contentful-paint')
  const freshData = resourceEntries.find((entry) => entry.name.includes(SURFACE_DATA_REQUEST))
  if (!firstRender || !freshData) return null
  return {
    surface: routeName,
    ms_to_first_render: Math.round(firstRender.startTime),
    ms_to_fresh: Math.round(freshData.responseEnd),
    shell_source: isControlledByServiceWorker ? 'service_worker' : 'network',
  }
}

/**
 * The profile counterpart of buildPageRenderProperties: the profile is read on every measured
 * surface, so it is reported for any load that landed on one.
 */
const buildProfileRenderProperties = ({
  routeName, paintEntries, resourceEntries, isControlledByServiceWorker,
}) => {
  if (!MEASURED_SURFACES.includes(routeName)) return null
  const firstRender = paintEntries.find((entry) => entry.name === 'first-contentful-paint')
  const freshProfile = resourceEntries.find((entry) => entry.name.includes(PROFILE_DATA_REQUEST))
  if (!firstRender || !freshProfile) return null
  return {
    surface: PROFILE_SURFACE,
    route: routeName,
    ms_to_first_render: Math.round(firstRender.startTime),
    ms_to_fresh: Math.round(freshProfile.responseEnd),
    shell_source: isControlledByServiceWorker ? 'service_worker' : 'network',
  }
}

/**
 * Records, at boot and before the flag wait, whether the worker controlled this page load and the
 * route the load opened on; by the time the worker registers, both may have changed.
 */
export const capturePageLoad = ({ router, serviceWorkerContainer = navigator.serviceWorker }) => {
  const pageLoad = { isControlledByServiceWorker: Boolean(serviceWorkerContainer?.controller), routeName: null }
  router.onReady(() => { pageLoad.routeName = router.currentRoute.name })
  return pageLoad
}

const trackRenderTimings = ({
  pageLoad, track, buildProperties, performance = window.performance,
}) => {
  if (typeof PerformanceObserver === 'undefined') return
  const { routeName: loadRouteName, isControlledByServiceWorker } = pageLoad
  if (!MEASURED_SURFACES.includes(loadRouteName)) return

  let observer = null
  // The resource timing buffer stops at 250 entries, so later entries reach only the observer.
  const observedEntries = { paint: [], resource: [] }
  const entriesOfType = (type) => [...performance.getEntriesByType(type), ...observedEntries[type]]
  const stop = () => observer?.disconnect()
  const sendWhenReady = () => {
    const properties = buildProperties({
      routeName: loadRouteName,
      paintEntries: entriesOfType('paint'),
      resourceEntries: entriesOfType('resource'),
      isControlledByServiceWorker,
    })
    if (!properties) return false
    stop()
    track(PAGE_RENDER_FROM_CACHE_EVENT, properties)
    return true
  }

  if (sendWhenReady()) return
  observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => observedEntries[entry.entryType]?.push(entry))
    sendWhenReady()
  })
  observer.observe({ entryTypes: ['resource', 'paint'] })
  setTimeout(stop, GIVE_UP_AFTER_MS)
}

/** Sends page_render_from_cache once for the load `capturePageLoad` recorded, as soon as the surface's data arrives. */
export const trackPageRenderFromCache = ({ pageLoad, track, performance }) => trackRenderTimings({
  pageLoad, track, performance, buildProperties: buildPageRenderProperties,
})

/** The same event for the profile: once the get-profile request of the recorded load has finished. */
export const trackProfileRenderFromCache = ({ pageLoad, track, performance }) => trackRenderTimings({
  pageLoad, track, performance, buildProperties: buildProfileRenderProperties,
})
